import React, { useEffect, useMemo, useState } from "react";
import useAutoScroll from "../../hooks/useAutoScroll";
import { Box, Typography, Button } from "@mui/material";
import CardMusicPlayer from "./CardMusicPlayer";
import MainPageCard from "./MainPageCard";
import WeddingDetails from "./WeddingDetails";
import CardBottomNav from "./CardBottomNav";
import CountingDay from "./CountingDay";
import GalleryCarousel from "./GalleryCarousel";
import WishSection from "./WishSection";
import "../../CSS/rsvpPopup.css";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CardOpening from "../CardView/CardOpening";
import { useParams } from "react-router-dom";
import { getCardBySlugAndCodeApi, getGiftsApi } from "../../api/cardApi";
import { getTranslations } from "../../utils/CardTranslations";

import CardParticles from "./CardParticles";
import LoadingScreen from "../LoadingScreen";
import { Camera } from "lucide-react";

const InvitationCardRender = () => {
    const { slug, uniqueCode } = useParams();

    const openPhotobooth = () => {
        window.open(
            `/photobooth/${encodeURIComponent(slug)}/${encodeURIComponent(uniqueCode)}`,
            '_blank',
            'noopener,noreferrer',
        );
    };

    const [cardData, setCardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cardOpened, setCardOpened] = useState(false);
    const [giftSubmissions, setGiftSubmissions] = useState([]);
    const [musicReady, setMusicReady] = useState(false);
    useAutoScroll(cardOpened);
    const t = getTranslations(cardData?.language);

    const theme = useMemo(() => createTheme({
        typography: {
            fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
        },
        components: {
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            '&.Mui-focused fieldset': {
                                borderColor: cardData?.buttonColour || cardData?.themeColour,
                            },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                            color: cardData?.buttonColour || cardData?.themeColour,
                        },
                    },
                },
            },
        },
    }), [cardData?.themeColour]);

    // Hide NavBar and Footer
    useEffect(() => {
        const navbar = document.querySelector(".main-navbar");
        const footer = document.querySelector(".main-footer");
        if (navbar) navbar.style.display = "none";
        if (footer) footer.style.display = "none";
        return () => {
            if (navbar) navbar.style.display = "";
            if (footer) footer.style.display = "";
        };
    }, []);

    // Fetch card data via API
    useEffect(() => {
        if (!slug || !uniqueCode) return;

        const fetchCard = async () => {
            try {
                const data = await getCardBySlugAndCodeApi(slug, uniqueCode);
                setCardData(data);
            } catch (err) {
                console.error("Error fetching invitation card:", err);
                setCardData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchCard();
    }, [slug, uniqueCode]);

    // Fetch gift submissions on mount — used to mark wishlist gifts as reserved
    useEffect(() => {
        if (!uniqueCode) return;
        getGiftsApi(uniqueCode)
            .then(setGiftSubmissions)
            .catch((err) => console.error("Error fetching gift submissions:", err));
    }, [uniqueCode]);

    // If no music URL is set, skip waiting for YouTube to load
    useEffect(() => {
        if (cardData && !cardData.musicYoutubeURL) {
            setMusicReady(true);
        }
    }, [cardData]);

    const designTemplate = cardData?.designTemplate;
    const isClassicTemplate = designTemplate === "classic";
    const designCode = cardData?.designCode;

    // Derive related data from the card payload (API embeds these)
    const programmes = cardData?.programmes ?? [];
    const contacts = cardData?.contactDetails ?? [];

    // Build Sets for reserved gift names and links (only non-empty values)
    const reservedNames = new Set(
        giftSubmissions.filter((s) => s.isReserved && s.giftName).map((s) => s.giftName)
    );
    const reservedLinks = new Set(
        giftSubmissions.filter((s) => s.isReserved && s.giftLink).map((s) => s.giftLink)
    );
    // Merge isReserved: true into wishlist gifts that match by name OR link
    const gifts = (cardData?.gifts ?? []).map((g) => ({
        ...g,
        isReserved: g.isReserved
            || (g.giftName ? reservedNames.has(g.giftName) : false)
            || (g.giftLink ? reservedLinks.has(g.giftLink) : false),
    }));

    // Re-fetch gift submissions so the UI reflects the latest reserve status
    const refreshGifts = async () => {
        try {
            const submissions = await getGiftsApi(uniqueCode);
            setGiftSubmissions(submissions);
        } catch (err) {
            console.error("Error refreshing gift submissions:", err);
        }
    };
    const images = [
        cardData?.sliderImage1,
        cardData?.sliderImage2,
        cardData?.sliderImage3,
    ].filter(Boolean); // removes empty/null slots

    /* --------------------------------
       Loading / Error states
    -------------------------------- */
    if (loading) {
        return <LoadingScreen />;
    }

    if (!cardData) {
        return (
            <Typography align="center" sx={{ mt: 8 }}>
                Jemputan tidak dijumpai.
            </Typography>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <div
                style={{
                    backgroundColor: "#f5f5f5",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    overflowY: "auto",
                }}
            >
                <div
                    style={{
                        position: "relative",
                        width: "100%",
                        maxWidth: "450px",
                        ...(isClassicTemplate
                            ? {
                                backgroundImage: `linear-gradient(rgba(226, 128, 47, 0.38)), url(/card-backgrounds/klasikFullBackground.jpg)`,
                                backgroundSize: "100% 100%",
                            }
                            : {
                                backgroundColor: cardData.themeBaseCardColour,
                            }
                        ),
                        boxShadow: "0 0 10px rgba(165, 13, 13, 0.1)",
                    }}
                >
                    <CardOpening
                        data={cardData}
                        openingStyle={cardData.openingStyle ?? "Tingkap A"}
                        openingColor={cardData.openingColor ?? "#f2f2f2"}
                        onOpen={() => setCardOpened(true)}
                        musicReady={musicReady}
                    >
                        <CardParticles data={cardData} />
                        <CardMusicPlayer data={cardData} playOnOpen={cardOpened} onMusicReady={() => setMusicReady(true)} />
                        <MainPageCard data={cardData} isOpened={cardOpened} />
                        <CardBottomNav
                            data={cardData}
                            contacts={contacts}
                            gifts={gifts}
                            source="api"
                            uniqueCode={uniqueCode}
                            onGiftReserved={refreshGifts}
                        />
                    </CardOpening>

                    {cardOpened && (
                        <>
                            <WeddingDetails data={cardData} programmes={programmes} />
                            <CountingDay data={cardData} images={images} />
                            {images.length > 0 && (
                                <GalleryCarousel images={images} />
                            )}
                            <WishSection data={cardData} source="api" uniqueCode={uniqueCode} />
                            {(cardData.packageType === 'Premium' || cardData.package === 'Premium') && (
                                <Box sx={{ px: 5, pt: 1, pb: 4, textAlign: 'center' }}>
                                    <Typography
                                        sx={{
                                            fontFamily: 'Poppins, sans-serif',
                                            fontSize: 15,
                                            fontWeight: 600,
                                            color: cardData.themeColour || '#45c0c0',
                                        }}
                                    >
                                        {t.photoboothTitle}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            mt: 0.75,
                                            fontFamily: 'Poppins, sans-serif',
                                            fontSize: 12,
                                            lineHeight: 1.7,
                                            color: 'text.secondary',
                                        }}
                                    >
                                        {t.photoboothDescription}
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        onClick={openPhotobooth}
                                        sx={{
                                            mt: 2,
                                            textTransform: "none",
                                            border: "none",
                                            color: cardData.themeColour,
                                            backgroundColor: cardData?.buttonColour || 'white',
                                            borderRadius: "50px",
                                            gap: 1,
                                            fontSize: "12px",
                                            px: "15px",
                                            py: "8px",
                                            boxShadow: '0 8px 16px 0 rgba(0,0,0,0.15)',
                                            "&:hover": {
                                                transition: "0.4s",
                                            },
                                        }}
                                    >
                                        <Camera size={19} />
                                        {t.photoboothButton}
                                    </Button>
                                </Box>
                            )}
                            <Box sx={{ px: 2, textAlign: "center", pb: "110px", display: "block" }}>
                                <Typography
                                    sx={{ fontFamily: "Poppins", fontSize: "13px" }}
                                    color={designCode === "floral-007" ? "white" : "text.secondary"}
                                >
                                    Created by
                                </Typography>
                                <Box
                                    component="a"
                                    href="https://www.sejiwa-my.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ justifyContent: "center", display: "flex", alignItems: "center", gap: 0.5, textDecoration: "none" }}
                                >
                                    <Box
                                        component="img"
                                        src="/sejiwa-logo-2.png"
                                        alt="Sejiwa"
                                        sx={{ width: "30px", height: "30px" }}
                                    />
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontFamily: '"Fredoka", "Inter", sans-serif',
                                            fontWeight: 400,
                                            letterSpacing: "1.5px",
                                            color: "#45C0C0",
                                        }}
                                    >
                                        Sejiwa
                                    </Typography>
                                </Box>
                            </Box>
                        </>
                    )}
                </div>


                {cardData.status === "PENDING PAYMENT" && (
                    <div
                        style={{
                            position: 'fixed',
                            opacity: 0.4,
                            top: 450,
                            left: 0,
                            right: 0,
                            background: 'linear-gradient(90deg,#6d1826,#c0395a)',
                            color: '#fff',
                            textAlign: 'center',
                            padding: '10px 0',
                            fontSize: '12px',
                            fontFamily: 'Poppins, sans-serif',
                            zIndex: 9999,
                            letterSpacing: '1px',
                            transition: 'opacity 1s ease',
                            pointerEvents: 'none',
                        }}
                    >
                        PREVIEW MODE - PENDING PAYMENT
                    </div>
                )}


            </div>
        </ThemeProvider>
    );
};

export default InvitationCardRender;
